import { useState, useEffect, useCallback, useRef } from 'react';
import { useCache } from '../contexts/CacheContext';

/**
 * Custom hook for cached API calls
 * @param {Function} apiFunction - The API function to call
 * @param {string} cacheKey - Unique key for caching this data
 * @param {Object} options - Configuration options
 * @returns {Object} - { data, loading, error, refetch, isFromCache }
 */
export const useCachedApi = (apiFunction, cacheKey, options = {}) => {
    const {
        dependencies = [],
        enabled = true,
        onSuccess,
        onError,
        skipCache = false,
    } = options;

    const { getCache, setCache } = useCache();
    const [data, setData] = useState([]);  // Initialize with empty array instead of null
    const [loading, setLoading] = useState(true);  // Start with loading true
    const [error, setError] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);
    const isMountedRef = useRef(true);
    const hasInitializedRef = useRef(false);

    const fetchData = useCallback(async (forceRefresh = false) => {
        if (!enabled) return;

        // Check cache first (unless force refresh or skip cache)
        if (!forceRefresh && !skipCache) {
            const cachedData = getCache(cacheKey);
            if (cachedData !== null) {
                setData(cachedData);
                setIsFromCache(true);
                setLoading(false);
                setError(null);
                if (onSuccess) onSuccess(cachedData);
                return;
            }
        }

        // Fetch from API
        setLoading(true);
        setIsFromCache(false);
        setError(null);

        try {
            const result = await apiFunction();

            if (isMountedRef.current) {
                setData(result);
                setLoading(false);

                // Cache the result
                if (!skipCache) {
                    setCache(cacheKey, result);
                }

                if (onSuccess) onSuccess(result);
            }
        } catch (err) {
            if (isMountedRef.current) {
                setError(err);
                setLoading(false);
                if (onError) onError(err);
            }
        }
    }, [apiFunction, cacheKey, enabled, getCache, setCache, skipCache, onSuccess, onError]);

    // Initial fetch
    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            fetchData();
        }
    }, [fetchData]);

    // Refetch when dependencies change
    useEffect(() => {
        if (hasInitializedRef.current && dependencies.length > 0) {
            fetchData(true); // Force refresh when dependencies change
        }
    }, dependencies);

    // Cleanup
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refetch = useCallback(() => {
        return fetchData(true);
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch,
        isFromCache
    };
};

/**
 * Hook for fetching data with manual trigger
 */
export const useLazyCachedApi = (apiFunction, cacheKey, options = {}) => {
    const { getCache, setCache } = useCache();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    const execute = useCallback(async (params, forceRefresh = false) => {
        const finalCacheKey = params ? `${cacheKey}:${JSON.stringify(params)}` : cacheKey;

        // Check cache first
        if (!forceRefresh && !options.skipCache) {
            const cachedData = getCache(finalCacheKey);
            if (cachedData !== null) {
                setData(cachedData);
                setIsFromCache(true);
                if (options.onSuccess) options.onSuccess(cachedData);
                return cachedData;
            }
        }

        setLoading(true);
        setIsFromCache(false);
        setError(null);

        try {
            const result = await apiFunction(params);
            setData(result);
            setLoading(false);

            // Cache the result
            if (!options.skipCache) {
                setCache(finalCacheKey, result);
            }

            if (options.onSuccess) options.onSuccess(result);
            return result;
        } catch (err) {
            setError(err);
            setLoading(false);
            if (options.onError) options.onError(err);
            throw err;
        }
    }, [apiFunction, cacheKey, getCache, setCache, options]);

    return {
        data,
        loading,
        error,
        execute,
        isFromCache
    };
};
